import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { queryClient } from '@/lib/react-query'
import { invalidateAllTeamQueries } from '@/utils/queryHelpers'
import {
  mergePersistedAuthStoreState,
  partializeAuthStorePersist,
} from '@/store/authStorePersist'

/**
 * État de reset de l'identité auth — partagé entre clearAuth (logout) et
 * l'échec de bootstrap session. Ne touche pas aux flags de transition.
 */
export const AUTH_RESET_STATE = {
  user: null,
  activeTeamId: null,
  activeTeamRole: null,
  globalRole: null,
  permissions: [],
  sessionRequiredPath: null,
  sessionDefaultPath: null,
  sessionHydrated: false,
  impersonating: false,
}

/**
 * Store unifié pour gérer l'authentification et le contexte d'équipe.
 * Session métier : GET /auth/session uniquement (cookies HttpOnly).
 */
export const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,

      activeTeamId: null,
      activeTeamRole: null,

      globalRole: null,
      permissions: [],

      sessionRequiredPath: null,
      sessionDefaultPath: null,
      sessionBootstrapped: false,
      sessionHydrated: false,
      sessionTransitioning: false,
      sessionSilentSwap: false,
      impersonating: false,

      setUser: (user) => set({ user }),
      setAuth: ({ user, role, permissions }) =>
        set({
          user,
          globalRole: role ?? null,
          permissions: permissions ?? [],
        }),
      setProfile: (user) => set({ user }),

      switchTeam: async (teamId) => {
        const { user, globalRole, activeTeamId: currentTeamId } = get()

        if (!user || !teamId || teamId === currentTeamId) return

        const { flushSync } = await import('react-dom')

        flushSync(() => {
          set({ sessionSilentSwap: true, activeTeamId: teamId })
        })

        await queryClient.cancelQueries()

        void import('@/lib/navigation/prefetchSidebarRoute').then(({ resetSidebarPrefetchCache }) => {
          resetSidebarPrefetchCache()
        })

        try {
          try {
            const { fetchActiveTeamSwitch, applyActiveTeamSwitchToClient } = await import(
              '@/lib/auth/hydrateFromAuthSession'
            )
            const switchPayload = await fetchActiveTeamSwitch(teamId)
            if (switchPayload) {
              applyActiveTeamSwitchToClient(switchPayload)
              invalidateAllTeamQueries()
              return
            }
          } catch (sessionError) {
            console.error('switchTeam active-team error:', sessionError)
          }

          try {
            const { fetchAndHydrateAuthSession } = await import('@/lib/auth/hydrateFromAuthSession')
            const hydrated = await fetchAndHydrateAuthSession()
            if (hydrated) {
              invalidateAllTeamQueries()
              return
            }
          } catch (sessionError) {
            console.error('switchTeam session refresh error:', sessionError)
          }

          invalidateAllTeamQueries()

          const cachedRole = queryClient.getQueryData(['teamRole', user.id, teamId])
          const fallbackRole = cachedRole || user.role || globalRole
          set({ activeTeamRole: fallbackRole })
          if (fallbackRole) {
            queryClient.setQueryData(['teamRole', user.id, teamId], fallbackRole)
          }
        } finally {
          flushSync(() => {
            set({ sessionSilentSwap: false, sessionHydrated: true })
          })
        }
      },

      initActiveTeam: async () => {
        const { user, activeTeamId, globalRole } = get()

        if (!user) return
        if (globalRole === 'super-admin' || user?.role === 'super-admin') return

        const teams = queryClient.getQueryData(['userTeams', user.id])

        if (!activeTeamId && Array.isArray(teams) && teams.length > 0) {
          await get().switchTeam(teams[0].id)
          return
        }

        if (activeTeamId) {
          const cachedRole = queryClient.getQueryData(['teamRole', user.id, activeTeamId])
          if (cachedRole) {
            set({ activeTeamRole: cachedRole })
          }
        }
      },

      clearAuth: async () => {
        const { resetSessionBootstrap } = await import('@/utils/bootstrapAppSession')
        const { useSessionUiStore } = await import('@/store/useSessionUiStore')
        resetSessionBootstrap()
        useSessionUiStore.getState().clear()
        await queryClient.cancelQueries()
        queryClient.removeQueries()
        queryClient.clear()
        set({
          ...AUTH_RESET_STATE,
          sessionBootstrapped: false,
          sessionTransitioning: false,
          sessionSilentSwap: false,
        })
      },

      setActiveTeamId: (teamId) => {
        get().switchTeam(teamId)
      },
      clearActiveTeam: () => set({ activeTeamId: null, activeTeamRole: null }),
    }),
    {
      name: 'auth-store',
      partialize: partializeAuthStorePersist,
      merge: mergePersistedAuthStoreState,
    }
  )
)

if (import.meta.hot) {
  const previousState = import.meta.hot.data?.authStoreState
  if (previousState) {
    useAuthStore.setState(previousState)
  }
  import.meta.hot.dispose((data) => {
    data.authStoreState = useAuthStore.getState()
  })
}
